import { nextTick } from 'vue';
import type { Router } from 'vue-router';
import { CONSENT_CHANGED_EVENT, type CookieConsent, loadConsent } from './consent';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

// Admin screens are internal tooling — keep them out of analytics.
const UNTRACKED_PATH = /^\/admin(\/|$)/;

// The only query params Google may see: campaign attribution. Everything else
// is dropped — an allowlist, because query strings here carry secrets
// (`?token=`, `?payment=`) and new ones will keep appearing.
const ATTRIBUTION_PARAM = /^(utm_[a-z]+|gclid|gbraid|wbraid|dclid)$/;

// Cookies set by gtag.js, split by the consent category that allows them.
const ANALYTICS_COOKIE = /^(_ga|_ga_.+|_gid|_gat(_.+)?)$/;
const MARKETING_COOKIE = /^(_gcl_.+|_gac_.+)$/;

let tagIds: string[] = [];
let loaded = false;
let currentPath: string | null = null;
let lastSentUrl: string | null = null;
// Current route sets its title after an async fetch (route meta
// `deferPageView`); its page view waits for `pageViewReady()`.
let deferred = false;

/**
 * Make a URL safe to hand to Google. Query strings here carry secrets —
 * `/reset-password?token=…`, `/verify-email?token=…`,
 * `/wallet/topup/return?payment=<ref>` — and gtag.js would otherwise read them
 * straight from `document.location` / `document.referrer`. Same-origin URLs
 * keep origin + path + attribution params (UTM, click IDs); cross-origin
 * referrers are reduced to their origin; unparsable input becomes ''.
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return u.origin;
    const kept = new URLSearchParams();
    u.searchParams.forEach((value, name) => {
      if (ATTRIBUTION_PARAM.test(name)) kept.append(name, value);
    });
    const query = kept.toString();
    return u.origin + u.pathname + (query ? `?${query}` : '');
  } catch {
    return '';
  }
}

/**
 * Expire Google cookies the visitor no longer consents to. gtag.js writes them
 * on the widest domain it can, so try every parent domain plus host-only.
 */
export function clearGoogleCookies(consent: CookieConsent) {
  const parts = window.location.hostname.split('.');
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join('.')}`);
  for (const pair of document.cookie.split(';')) {
    const name = pair.split('=')[0].trim();
    const denied =
      (!consent.analytics && ANALYTICS_COOKIE.test(name)) || (!consent.marketing && MARKETING_COOKIE.test(name));
    if (!denied) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
}

function consentSignals(c: CookieConsent) {
  const analytics = c.analytics ? 'granted' : 'denied';
  const marketing = c.marketing ? 'granted' : 'denied';
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  };
}

function gtag(...args: unknown[]) {
  window.gtag?.(...args);
}

/**
 * Override gtag.js's own reads of `document.location` / `document.referrer`
 * for every hit it sends, including automatic ones (engagement, scroll).
 * The first referrer is the external one; SPA navigations refer to the
 * previous in-app page.
 */
function setSafeLocation(fullPath: string) {
  gtag('set', {
    page_location: sanitizeUrl(window.location.origin + fullPath),
    page_referrer: lastSentUrl ?? sanitizeUrl(document.referrer),
  });
}

function sendPageView(fullPath: string) {
  const location = sanitizeUrl(window.location.origin + fullPath);
  const path = location.slice(window.location.origin.length);
  // Consecutive identical URLs are one view — e.g. verify/reset pages
  // replacing their token-bearing URL with the bare path.
  if (UNTRACKED_PATH.test(path) || location === lastSentUrl) return;
  setSafeLocation(fullPath);
  gtag('event', 'page_view', {
    page_path: path,
    page_location: location,
    page_title: document.title,
  });
  lastSentUrl = location;
}

/**
 * Nothing is fetched from Google until the visitor opts in to analytics or
 * marketing (Consent Mode v2, basic implementation). Consent defaults are set
 * to denied before gtag.js loads so the tag never runs ahead of the choice.
 */
function load(consent: CookieConsent) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the `arguments` object itself, not a copied array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  gtag('consent', 'update', consentSignals(consent));
  gtag('js', new Date());
  // Before any config: gtag.js must never see the raw URL (it may hold a token).
  setSafeLocation(currentPath ?? window.location.pathname);
  // Page views are sent from the router so SPA navigations are counted once.
  for (const id of tagIds) gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagIds[0])}`;
  document.head.appendChild(script);
  loaded = true;

  if (currentPath && !deferred) sendPageView(currentPath);
}

function applyConsent(consent: CookieConsent | null) {
  if (!consent) return;
  // Runs even with no tags configured: cookies from an earlier visit (or a
  // since-removed tag ID) must go once consent is withdrawn.
  clearGoogleCookies(consent);
  if (!tagIds.length) return;
  if (loaded) {
    gtag('consent', 'update', consentSignals(consent));
  } else if (consent.analytics || consent.marketing) {
    load(consent);
  }
}

/** Called once the public settings arrive with the per-server tag IDs. */
export function configureGoogleTags(ids: string[]) {
  if (loaded) return;
  tagIds = ids;
  applyConsent(loadConsent());
}

/** Wire page-view tracking and consent updates. Call once at app start. */
export function installGoogleTags(router: Router) {
  router.afterEach((to, _from, failure) => {
    if (failure) return;
    // Raw fullPath is kept locally only; sanitizeUrl runs before anything is
    // sent, so attribution params survive and tokens never leave.
    const fullPath = to.fullPath;
    currentPath = fullPath;
    deferred = Boolean(to.meta.deferPageView);
    // Wait for the new view to render so the leaving page has restored
    // document.title.
    if (loaded && !deferred) void nextTick(() => sendPageView(fullPath));
  });
  window.addEventListener(CONSENT_CHANGED_EVENT, (e) => {
    applyConsent((e as CustomEvent<CookieConsent>).detail);
  });
}

/**
 * For `deferPageView` routes: call once the view has set its real
 * document.title (or failed to load). Sends the held page view.
 */
export function pageViewReady() {
  if (!deferred) return;
  deferred = false;
  if (loaded && currentPath) sendPageView(currentPath);
}

/** Fire a GA4/Ads event. No-op until the tag is loaded (i.e. consent given). */
export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (loaded) gtag('event', name, params);
}
